import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Pressable,
  Image,
  ActivityIndicator,
  ImageBackground,
  Dimensions,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../contexts/AuthContext';
import { runAppVersionCheck } from '../services/appVersionCheck';
import { getCurrentProvider } from '../services/auth/authServiceFactory';
import { networkService } from '../services/network';
import { openExternalLink } from '../utils/inAppBrowser';

interface LoginProps {
  onRequestLogin?: () => void;
  /**
   * Authenticated user has no ITS application roles — show denial on the same
   * login shell (ITS_TC_101). No separate /unauthorized route for this case.
   */
  accessNotAuthorized?: boolean;
}

export const Login: React.FC<LoginProps> = ({
  onRequestLogin,
  accessNotAuthorized = false,
}) => {
  const [localError, setLocalError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const authContext = useAuth();
  const { login, isLoading, error, clearError, logout, user } = authContext;
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isLargeScreen = isWeb && screenData.width >= 1024;
  const authProvider = getCurrentProvider();

  // Check for auth_error in URL params on mount (from OAuth callback failures)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const authError = urlParams.get('auth_error');
      if (authError) {
        setLocalError(decodeURIComponent(authError));
        // Clean up URL by removing the error param (without triggering navigation)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  // Clear errors when component becomes visible (user navigates back from registration)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden && error) {
        clearError();
      }
    };

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }, [error, clearError]);

  useEffect(() => {
    if (isWeb) {
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setScreenData(window);
      });

      return () => subscription?.remove();
    }
  }, [isWeb]);

  // Network connectivity monitoring
  // Note: networkService.initialize() is already called in App.tsx via initializeOfflineServices()
  // We only need to subscribe to state changes here
  useEffect(() => {
    // Get initial state (networkService is already initialized by App.tsx)
    setIsOnline(networkService.isConnected());

    const unsubscribe = networkService.addListener((connected) => {
      setIsOnline(connected);
    });

    return () => {
      unsubscribe();
    };
  }, []);


  const handleVertivAccountLink = async () => {
    const url = 'https://partners.vertiv.com/';
    await openExternalLink(url);
  };

  const handleLogin = async () => {
    setLocalError(null);
    clearError(); // Clear any previous auth context errors

    // Authenticated session but no ITS roles — stay on login shell (ITS_TC_101); Log In is inert.
    if (accessNotAuthorized) {
      return;
    }

    try {
      if (!login) {
        throw new Error('login function is not available');
      }

      if ((await runAppVersionCheck()) === 'blocked') {
        return;
      }

      await login(); // OAuth-based login doesn't require userId parameter
      // Navigation will be handled by App.tsx based on auth state
    } catch (err) {
      // Error is already set in auth context
      console.error(`${authProvider} login failed:`, err);
      console.error('Error message:', err instanceof Error ? err.message : 'Unknown error');
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setLocalError(errorMessage);
    }
  };

  // Simple login button text - provider-agnostic
  const getLoginButtonText = () => {
    return 'Log In';
  };

  const displayError = localError || error;
  const isRegistrationError = error && error.includes('Please use the "Request Registration" link below to register');

  const renderNoRoleBanner = () => (
    <View style={styles.noRoleBanner}>
      <Text testID="unauthorized-access-title" style={styles.noRoleBannerTitle}>
        Access Not Authorized
      </Text>
      <Text style={styles.accessDeniedSupport}>
        You don't have the necessary permissions to access the Vertiv Site Scope.
      </Text>
      {user && (
        <View style={styles.userInfoBox}>
          <Text style={styles.userInfoLabel}>Logged in as:</Text>
          <Text style={styles.userInfoText}>{user.name}</Text>
          <Text style={styles.userInfoText}>{user.email}</Text>
        </View>
      )}
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={[
        styles.content,
        isWeb && { height: screenData.height },
        isLargeScreen ? styles.horizontalLayout : styles.verticalLayout
      ]}>
        <View style={[
          styles.imageSection,
          isLargeScreen ? styles.imageSectionHorizontal : styles.imageSectionVertical
        ]}>
          <ImageBackground
            source={require('../assets/images/datacenter-background.png')}
            style={styles.backgroundImage}
            resizeMode="cover"
          >
            <View style={styles.overlay} />
          </ImageBackground>
        </View>
        {isLargeScreen ? (
          <View style={styles.contentSectionHorizontal}>
            <View style={styles.contentCard}>
              <>
                <View style={styles.logoContainer}>
                  <Image
                    testID="vertiv-logo"
                    source={require('../assets/vertiv-logo-black.png')}
                    style={styles.logo as any}
                    resizeMode="contain"
                  />
                </View>

                <Text testID="app-title" style={styles.title}>Vertiv Site Scope</Text>

                <View style={styles.formContainer}>
                  {!isOnline && (
                    <View style={styles.offlineContainer}>
                      <Text style={styles.offlineText}>
                        You're currently offline. Please connect to the internet to log in.
                      </Text>
                    </View>
                  )}

                  {accessNotAuthorized && renderNoRoleBanner()}

                  {displayError && (
                    <View style={styles.errorContainer}>
                      <Text style={[styles.errorText, isRegistrationError && styles.registrationErrorText]}>
                        {displayError}
                      </Text>
                    </View>
                  )}

                  {/* OAuth Login Button (Okta) — no-op when accessNotAuthorized (ITS_TC_101) */}
                  <Pressable
                    testID="login-button"
                    style={[styles.loginButton, (isLoading || !isOnline) ? styles.loginButtonDisabled : null]}
                    onPress={handleLogin}
                    disabled={isLoading || !isOnline}
                  >
                    {isLoading ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={styles.loginButtonText}>{getLoginButtonText()}</Text>
                    )}
                  </Pressable>

                  {accessNotAuthorized && (
                    <Pressable
                      testID="login-screen-sign-out-no-its-role"
                      style={styles.signOutLink}
                      onPress={() => void logout()}
                      disabled={isLoading}
                    >
                      <Text style={styles.signOutLinkText}>Sign Out</Text>
                    </Pressable>
                  )}

                  {/* Divider */}
                  <View style={styles.dividerContainer}>
                    <View style={styles.dividerLine} />
                    <Text testID="divider-text" style={styles.dividerText}>or</Text>
                    <View style={styles.dividerLine} />
                  </View>

                  <View style={styles.vertivAccountContainer}>
                    <Text testID="account-prompt" style={styles.vertivAccountText}>Need a Vertiv account? </Text>
                    <Pressable onPress={handleVertivAccountLink}>
                      <Text testID="click-here-link" style={styles.vertivAccountLink}>Click here</Text>
                    </Pressable>
                  </View>
                </View>
              </>
            </View>
          </View>
        ) : (
          <View style={styles.contentSectionVertical}>
            <View style={styles.contentCardVertical}>
              <>
                <View style={styles.logoContainer}>
                  <Image
                    testID="vertiv-logo"
                    source={require('../assets/vertiv-logo-black.png')}
                    style={styles.logo as any}
                    resizeMode="contain"
                  />
                </View>

                <Text testID="app-title" style={styles.title}>Vertiv Site Scope</Text>

                <View style={styles.formContainer}>
                  {!isOnline && (
                    <View style={styles.offlineContainer}>
                      <Text style={styles.offlineText}>
                        You're currently offline. Please connect to the internet to log in.
                      </Text>
                    </View>
                  )}

                  {accessNotAuthorized && renderNoRoleBanner()}

                  {displayError && (
                    <View style={styles.errorContainer}>
                      <Text style={[styles.errorText, isRegistrationError && styles.registrationErrorText]}>
                        {displayError}
                      </Text>
                    </View>
                  )}

                  {/* OAuth Login Button (Okta) — no-op when accessNotAuthorized (ITS_TC_101) */}
                  <Pressable
                    testID="login-button"
                    style={[styles.loginButton, (isLoading || !isOnline) ? styles.loginButtonDisabled : null]}
                    onPress={handleLogin}
                    disabled={isLoading || !isOnline}
                  >
                    {isLoading ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={styles.loginButtonText}>{getLoginButtonText()}</Text>
                    )}
                  </Pressable>

                  {accessNotAuthorized && (
                    <Pressable
                      testID="login-screen-sign-out-no-its-role"
                      style={styles.signOutLink}
                      onPress={() => void logout()}
                      disabled={isLoading}
                    >
                      <Text style={styles.signOutLinkText}>Sign Out</Text>
                    </Pressable>
                  )}

                  {/* Divider */}
                  <View style={styles.dividerContainer}>
                    <View style={styles.dividerLine} />
                    <Text testID="divider-text" style={styles.dividerText}>or</Text>
                    <View style={styles.dividerLine} />
                  </View>

                  <View style={styles.vertivAccountContainer}>
                    <Text testID="account-prompt" style={styles.vertivAccountText}>Need a Vertiv account? </Text>
                    <Pressable onPress={handleVertivAccountLink}>
                      <Text testID="click-here-link" style={styles.vertivAccountLink}>Click here</Text>
                    </Pressable>
                  </View>
                </View>
              </>
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  horizontalLayout: {
    flexDirection: 'row',
  },
  verticalLayout: {
    flexDirection: 'column',
    ...Platform.select({
      web: {
        position: 'relative',
      },
    }),
  },
  imageSection: {
    backgroundColor: '#f0f0f0',
  },
  imageSectionHorizontal: {
    flex: 1,
  },
  imageSectionVertical: {
    height: 300,
    ...Platform.select({
      web: {
        height: '40vh' as any,
        minHeight: 250,
      },
    }),
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  contentSectionHorizontal: {
    flex: 1,
    paddingHorizontal: 60,
    paddingVertical: 40,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSectionVertical: {
    paddingHorizontal: 24,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 40,
      },
      android: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 30,
      },
      web: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  contentCard: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 8,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
      },
    }),
  },
  contentCardVertical: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    marginHorizontal: 0,
    maxWidth: 400,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      ios: {
        paddingVertical: 40,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        paddingVertical: 36,
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        paddingHorizontal: 32,
        paddingVertical: 40,
        overflow: 'hidden',
      },
    }),
  },
  logoContainer: {
    marginBottom: 32,
    alignItems: 'center',
  },
  logo: {
    width: 80,
    height: 80,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 40,
    color: '#1a1a1a',
    lineHeight: 28,
  },
  formContainer: {
    width: '100%',
  },
  loginButton: {
    backgroundColor: '#FF6B35',
    borderRadius: 4,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 16,
    width: '100%',
  },
  loginButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  mockLoginButton: {
    backgroundColor: '#FF6B35',
    borderRadius: 4,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 16,
    width: '100%',
  },
  mockLoginButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#ddd',
  },
  dividerText: {
    marginHorizontal: 16,
    color: '#666',
    fontSize: 14,
    fontWeight: '500',
  },
  requestContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  requestText: {
    fontSize: 14,
    color: '#666',
  },
  requestLink: {
    fontSize: 14,
    color: '#FF6B35',
    fontWeight: '500',
  },
  vertivAccountContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  vertivAccountText: {
    fontSize: 14,
    color: '#666',
  },
  vertivAccountLink: {
    fontSize: 14,
    color: '#FF6B35',
    fontWeight: '500',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  errorContainer: {
    marginTop: -20,
    marginBottom: 16,
  },
  errorText: {
    color: '#dc3545',
    fontSize: 13,
  },
  registrationErrorText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 12,
  },
  loginButtonDisabled: {
    backgroundColor: '#FFA57C',
    opacity: 0.7,
  },
  offlineContainer: {
    backgroundColor: '#FFF3CD',
    borderWidth: 1,
    borderColor: '#FFE69C',
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
  },
  offlineText: {
    color: '#856404',
    fontSize: 14,
    textAlign: 'center',
  },
  noRoleBanner: {
    width: '100%',
    marginBottom: 8,
  },
  noRoleBannerTitle: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    color: '#721c24',
    marginBottom: 10,
  },
  signOutLink: {
    alignSelf: 'center',
    marginTop: 4,
    marginBottom: 12,
    paddingVertical: 8,
  },
  signOutLinkText: {
    color: '#555',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  accessDeniedSupport: {
    fontSize: 15,
    color: '#444',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 22,
  },
  userInfoBox: {
    width: '100%',
    marginBottom: 20,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f7f7f9',
    borderRadius: 8,
  },
  userInfoLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  userInfoText: {
    fontSize: 14,
    color: '#1a1a1a',
  },
});